import React from 'react';
import { Amplify } from '@aws-amplify/core';
import { config as amplifyConfig } from '@/amplify';
import { initSentry } from '@/utils/SentrySetup';
import { initPostHog } from '@/utils/analytics/posthog';
import { createRoot } from 'react-dom/client';
import ServiceWorkerLoader from '@/modules/app/ServiceWorkerLoader';
import { Router } from 'react-router';
import { createBrowserHistory } from 'history';
import { AuthMachineProvider } from '@/utils/auth';
import GraphQLProvider from '@/graphql/graphql';
import App from './AppMaquinista';
import { InstallProvider } from '@/hooks/usePWAInstall';
import './react_11538_workaround';
import AppErrorBoundary from '@/components/AppErrorBoundary';
import { Provider as JotaiProvider } from 'jotai';
import { BilderitRbacManager } from '@/hooks/BilderitRbacProvider';
import { WebPushHandler } from '@/utils/WebPushHandler';
import { TooltipProvider } from '@/components/ui/tooltip';
import { SoftKeyboardListener } from '@/hooks/useIsKeyboardOpen';
import { ThemeProvider } from '@/components/theme-provider';
import { Toaster } from '@/components/ui/sonner';
import { NuqsAdapter } from 'nuqs/adapters/react';
import { Root as PortalRoot } from '@radix-ui/react-portal';
import { listenForHostedUIMergeSignIn } from '@/utils/identityClaimSession';
import { listenForHostedUIFailure } from '@/utils/hostedUIFailure';

const getBasename = () => {
  return `/${import.meta.env.VITE_PUBLIC_URL?.split('/').pop() || ''}`;
};
const history = createBrowserHistory({ basename: getBasename() });
initSentry(history);
initPostHog();

// Amplify must be configured before the first render — useCurrentIdentity()
// calls Auth.currentAuthenticatedUser() at mount, above the auth provider in
// the tree. It used to be configured in an AuthMachineProvider effect, which
// mounted AFTER the identity gate, so every boot resolved the identity as
// null first (cache never restored on a signed-in reload). Configured here,
// in the entry, rather than at amplify.ts module scope so the test
// environment (which imports amplify.ts transitively but never the entries)
// is unaffected.
listenForHostedUIMergeSignIn();
listenForHostedUIFailure();
Amplify.configure(amplifyConfig);

// if (import.meta.env.MODE !== 'production') {
//   const whyDidYouRender = require('@welldone-software/why-did-you-render');
//   whyDidYouRender(React, {
//     logOnDifferentValues: true,
//     include: [/PureTable/],
//   });
// }

const container = document.getElementById('root');
const root = createRoot(container); // createRoot(container!) if you use TypeScript
root.render(
  <ThemeProvider>
    <AppErrorBoundary>
      <NuqsAdapter>
        <PortalRoot>
          <Toaster position="top-right" className="pointer-events-auto" />
        </PortalRoot>
        <SoftKeyboardListener />
        <JotaiProvider>
          <ServiceWorkerLoader />
          <Router history={history}>
            <GraphQLProvider>
              <AuthMachineProvider>
                <WebPushHandler />
                <BilderitRbacManager />
                <InstallProvider>
                  <TooltipProvider>
                    <AppErrorBoundary>
                      <App />
                    </AppErrorBoundary>
                  </TooltipProvider>
                </InstallProvider>
              </AuthMachineProvider>
            </GraphQLProvider>
          </Router>
        </JotaiProvider>
      </NuqsAdapter>
    </AppErrorBoundary>
  </ThemeProvider>
);
